import { QueryClient, useQuery } from '@tanstack/react-query';
import { api } from '../utils/api';
import { ApiError, ApiResponse } from '../types/api';
import { useVegetationTileVersion } from './vegetation';

/**
 * A project's tree / shrub crowns, columnar: parallel arrays, one entry per
 * crown. `kind` and `role` are small integer codes (`kind_codes`,
 * `role_codes` say which). Crowns are packed from the fine-detail Tree /
 * Shrub polygons on the backend (api.shared.crown_packing): a single tree,
 * the trees of a hedge, or a lattice over a clump — the 3D layer draws every
 * one at its imagery position, and the fire graph runs the ones with a
 * `role` other than interior.
 */
export interface CrownsPayload {
  count: number;
  id: number[];
  lng: number[];
  lat: number[];
  radius_m: number[];
  height_m: number[];
  kind: number[];
  role: number[];
  kind_codes: Record<string, number>;
  role_codes: Record<string, number>;
}

export const CROWN_KIND_TREE = 1;
export const CROWN_KIND_SHRUB = 2;
export const CROWN_ROLE_INTERIOR = 3;

export const useCrowns = (projectId: string | undefined, enabled: boolean) => {
  // Crowns change when the vegetation does (an editor apply re-packs them),
  // and that is when the vegetation tiles are re-versioned too.
  const tileVersion = useVegetationTileVersion(projectId);

  return useQuery<CrownsPayload | null, ApiError>({
    queryKey: ['crowns', projectId, tileVersion],
    enabled: Boolean(projectId && enabled),
    queryFn: async () => {
      if (!projectId) return null;
      const response = await api.get<ApiResponse<CrownsPayload>>(`/api/projects/${projectId}/crowns`);
      return response.data;
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

/**
 * How a project's Tree / Shrub polygons are read as crowns. Mirrors
 * `CrownSettings` in the backend (api/shared/crown_packing.py): a project
 * stores only the keys it overrides in `Project.crown_settings`; an empty
 * object means the defaults below. All values are metres.
 */
export const CROWN_SETTING_DEFAULTS = {
  r_clump_tree_m: 8,
  r_clump_shrub_m: 3,
  spacing_tree_m: 7,
  spacing_shrub_m: 3.5,
  height_clump_tree_m: 12,
  height_clump_shrub_m: 1.5,
  height_single_shrub_m: 1.5,
} as const;

export type CrownSettingKey = keyof typeof CROWN_SETTING_DEFAULTS;
export type CrownSettingsOverrides = Partial<Record<CrownSettingKey, number>>;

/** The stored overrides, dropping unknown keys and anything that is not a positive number. */
export const cleanCrownOverrides = (raw: unknown): CrownSettingsOverrides => {
  const out: CrownSettingsOverrides = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const key of Object.keys(CROWN_SETTING_DEFAULTS) as CrownSettingKey[]) {
    const value = (raw as Record<string, unknown>)[key];
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) out[key] = value;
  }
  return out;
};

export const crownOverridesEqual = (a: CrownSettingsOverrides, b: CrownSettingsOverrides): boolean => {
  const keys = Object.keys(CROWN_SETTING_DEFAULTS) as CrownSettingKey[];
  return keys.every((k) => (a[k] ?? null) === (b[k] ?? null));
};

const fetchCrowns = async (projectId: string): Promise<CrownsPayload> => {
  const response = await api.get<ApiResponse<CrownsPayload>>(`/api/projects/${projectId}/crowns`);
  return response.data;
};

/** A cheap signature of the packed crowns: changes whenever a re-pack lands. */
export const fetchCrownFingerprint = async (projectId: string): Promise<string | null> => {
  try {
    const p = await fetchCrowns(projectId);
    let sum = 0;
    for (const r of p.radius_m) sum += r;
    return `${p.count}|${sum.toFixed(2)}`;
  } catch {
    return null;
  }
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Saving `crown_settings` re-packs the project's crowns in a background task,
 * and nothing tells the browser when that lands. Poll the crowns endpoint
 * until its fingerprint differs from `before` (taken before the save), then
 * refresh every crowns query so the 3D layer redraws. Resolves true when the
 * re-pack was seen, false on timeout; the queries are refreshed either way.
 */
export const waitForCrownRepack = async (
  queryClient: QueryClient,
  projectId: string,
  before: string | null,
  { timeoutMs = 90_000, intervalMs = 3_000 }: { timeoutMs?: number; intervalMs?: number } = {},
): Promise<boolean> => {
  const deadline = Date.now() + timeoutMs;
  let seen = false;
  while (Date.now() < deadline) {
    await sleep(intervalMs);
    const now = await fetchCrownFingerprint(projectId);
    if (now !== null && now !== before) {
      seen = true;
      break;
    }
  }
  queryClient.invalidateQueries({ queryKey: ['crowns', projectId] });
  return seen;
};
